import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Star, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /review/:token — one-tap review landing.
 *
 * Behaviour:
 *   - On mount, read ?r=N (1-5) from the email's star link and AUTO-COMMIT.
 *     The buyer never sees a "submit" gate for the star rating itself —
 *     that's the whole point of one-tap.
 *   - After auto-commit, the page invites an optional title + body. The
 *     buyer can leave them blank; the rating already counted.
 *   - For 4-5★ ratings we display "Your review is live" copy; for 1-3★
 *     we display "Thanks — our team will review your feedback shortly"
 *     because those land in the moderation queue.
 */
export default function ReviewLandingPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const initialRating = parseInt(params.get('r') || '0', 10);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(null);
  const [rating, setRating] = useState(initialRating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/reviews/token/${token}`)
      .then(res => { if (alive) setMeta(res.data); })
      .catch(() => { if (alive) setMeta({ error: true }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  // Auto-commit on landing when ?r=N was supplied by the email.
  useEffect(() => {
    if (!meta || meta.error || submitted) return;
    if (initialRating < 1 || initialRating > 5) return;
    (async () => {
      try {
        const res = await axios.post(`${API}/reviews/${token}`, { rating: initialRating });
        setSubmitted(res.data);
        setRating(initialRating);
      } catch {
        // silent — buyer can still pick a star manually
      }
    })();
  }, [meta, initialRating, token, submitted]);

  const submit = async () => {
    if (rating < 1 || rating > 5) {
      toast.error('Pick a star rating first');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/reviews/${token}`, {
        rating,
        title: title.trim() || undefined,
        body: body.trim() || undefined,
      });
      setSubmitted(res.data);
      toast.success(res.data.auto_published ? 'Your review is live ✨' : 'Thanks — our team will review your feedback shortly.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your review');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-20">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!meta || meta.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-20 px-4" data-testid="review-link-expired">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-2">
            <h2 className="text-lg font-bold">Link expired</h2>
            <p className="text-sm text-muted-foreground">This review link is no longer valid. If you'd like to share feedback, reach out at <a className="underline" href="mailto:team@eventism.com">team@eventism.com</a>.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ev = meta.event || {};
  const kindPrefix = meta.kind === 'experience' ? '/ex' : (meta.kind === 'reservation' ? '/res' : '/events');
  const eventUrl = `${kindPrefix}/${ev.city || 'denver'}/${ev.slug || ev.event_id || ''}`;

  return (
    <div className="min-h-screen bg-background pt-20 pb-16 px-4" data-testid="review-landing">
      <div className="max-w-xl mx-auto">
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-start gap-4">
              {ev.image_url && (
                <img src={ev.image_url} alt="" className="h-16 w-16 rounded-lg object-cover flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">How was it?</p>
                <h1 className="text-xl sm:text-2xl font-bold mt-0.5" data-testid="review-event-name">{ev.name}</h1>
                <p className="text-xs text-muted-foreground mt-1">
                  Hi {(meta.reviewer_name || 'there').split(' ')[0]}, your feedback helps future guests.
                </p>
              </div>
            </div>

            {/* Star picker */}
            <div className="flex items-center justify-center gap-2 py-4" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map(n => {
                const filled = (hoverRating || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(n)}
                    aria-label={`${n} star${n === 1 ? '' : 's'}`}
                    data-testid={`review-star-${n}`}
                    className="transition-transform hover:scale-110"
                  >
                    <Star className={`h-9 w-9 ${filled ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />
                  </button>
                );
              })}
            </div>

            {submitted && (
              <div className={`rounded-lg border p-3 text-sm flex gap-2 ${submitted.auto_published ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'}`} data-testid="review-auto-confirm">
                <CheckCircle2 className={`h-4 w-4 flex-shrink-0 mt-0.5 ${submitted.auto_published ? 'text-emerald-500' : 'text-amber-500'}`} />
                <div>
                  <p className="font-medium">
                    {submitted.auto_published ? 'Your rating is live.' : 'Got it — thanks for the candid feedback.'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {submitted.auto_published
                      ? 'Optional: add a short comment below to help future guests.'
                      : 'Our team will review your comment within 24 hours before publishing.'}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Sum it up in a sentence (optional)"
                maxLength={160}
                data-testid="review-title"
              />
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="Anything else you'd like future guests to know? (optional)"
                data-testid="review-body"
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <Link to={eventUrl} className="text-xs text-muted-foreground hover:underline">← View listing</Link>
              <Button onClick={submit} disabled={saving || rating < 1} data-testid="review-submit-btn">
                {saving ? 'Saving…' : (submitted ? 'Update review' : 'Submit review')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
